import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { createSelector } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';

import { selectResources } from '@/ducks/common/selectors';
import { selectCruisesCommonVoyagesDataRegions } from '@/ducks/cruisesCommon/selectors';
import {
  selectFromDate,
  selectSelectedItinerariesRegions,
  selectSelectedPortsOfCallRegions,
  selectToDate,
} from '@/ducks/filters/selectors';
import { pickAnyFromSearchParams } from '@/ducks/pages/navigation';
import { isServerSide } from '@/helpers/isServerSide';
import { formatHumanReadableList, getStoredValue, setStoredValue } from '@/helpers/util';
import { useAppSelector } from '@/store';

const RECENT_SEARCHES = 'recentSearches';

const selectTitleWithAllRegions = createSelector(
  [
    selectResources,
    selectSelectedItinerariesRegions,
    selectSelectedPortsOfCallRegions,
    selectCruisesCommonVoyagesDataRegions,
  ],
  (resources, packagesRegionsIds, portsRegionsId, allRegions) => {
    const selectedRegionsIds = new Set([...packagesRegionsIds, ...portsRegionsId]);
    const allRegionsLabel = resources?.['Destination.filter.text'] || 'All regions';
    if (selectedRegionsIds.size === 0 || allRegions?.length === selectedRegionsIds.size) {
      return allRegionsLabel as string;
    }

    return formatHumanReadableList(allRegions.filter(({ id }) => selectedRegionsIds.has(id)).map(({ name }) => name));
  },
);

type SearchData = {
  filtersSearchParams: Record<string, number | string>;
  searchTime: string;
  title: string;
  uuid?: null | string;
};

export const useRecentSearchesSaver = () => {
  const searchParams = useSearchParams();
  const voyageId = searchParams.get('voyageId');
  const title = useAppSelector(selectTitleWithAllRegions);
  const dateFrom = useAppSelector(selectFromDate);
  const dateTo = useAppSelector(selectToDate);

  useEffect(() => {
    if (!isServerSide()) {
      const savedSearches: SearchData[] = getStoredValue(RECENT_SEARCHES) || [];
      const currentSearch = pickAnyFromSearchParams({ withFilters: true });

      const searchData: SearchData = {
        filtersSearchParams: { dateFrom, dateTo, ...currentSearch },
        searchTime: new Date().toISOString(),
        title,
        // Used for tracking (Saving which voyage user has clicked)
        uuid: voyageId,
      };

      const isAlreadySaved = savedSearches.find(({ filtersSearchParams }) =>
        isEqual(filtersSearchParams, searchData.filtersSearchParams),
      );

      if (isAlreadySaved) return;

      const data = [searchData, ...savedSearches.slice(0, 2)];
      setStoredValue(RECENT_SEARCHES, data);

      // Update value in account dropdown
      const accountDropdown = document.querySelector('account-dropdown');
      accountDropdown?.setAttribute('recent-searches', JSON.stringify(data));
    }
  }, [dateFrom, dateTo, title, voyageId]);
};
